package app.nestbridge.feature.children.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Email
import androidx.compose.material.icons.filled.Group
import androidx.compose.material.icons.filled.QrCode2
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.components.CoreCard
import com.karawek.core.ui.components.CoreLoadingIndicator
import com.karawek.core.ui.components.CoreNoticeCard
import com.karawek.core.ui.components.CoreNoticeTone
import com.karawek.core.ui.components.CoreScaffold
import com.karawek.core.ui.components.CoreSecondaryButton
import com.karawek.core.ui.components.CoreTextButton
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import com.karawek.core.util.PhoneNumberFormatter
import app.nestbridge.domain.identity.model.ProfessionalServiceType
import app.nestbridge.feature.careteam.model.CareTeam
import app.nestbridge.feature.children.model.Child
import app.nestbridge.feature.children.model.ChildStatus
import app.nestbridge.feature.children.screen.ChildDetailEvent
import app.nestbridge.feature.children.screen.ChildDetailScreen
import app.nestbridge.feature.children.screen.ChildDetailState
import app.nestbridge.feature.invitations.model.Invitation
import app.nestbridge.feature.invitations.model.InvitationDeliveryMethod
import app.nestbridge.feature.invitations.model.InvitationStatus
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import kotlinx.datetime.LocalDate
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import kotlin.time.Clock

@CircuitInject(
    screen = ChildDetailScreen::class,
    scope = AppScope::class,
)
@Composable
fun ChildDetailUi(
    state: ChildDetailState,
    modifier: Modifier = Modifier,
) {
    CoreScaffold(
        title = state.child.effectiveDisplayName,
        subtitle = "Child profile details",
        onBack = { state.eventSink(ChildDetailEvent.Back) },
        onAppear = { state.eventSink(ChildDetailEvent.OnAppear) },
        onAppearKey = state.child.id,
        modifier = modifier,
    ) {
        if (state.isLoading) {
            CoreLoadingIndicator()
        } else {
            Column(
                verticalArrangement = Arrangement.spacedBy(CoreSpacing.lg),
                modifier = Modifier.fillMaxSize(),
            ) {
                // Child Information Card
                CoreCard(
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Column(
                        modifier = Modifier.padding(CoreSpacing.md),
                        verticalArrangement = Arrangement.spacedBy(CoreSpacing.md),
                    ) {
                        Text(
                            text = "CHILD INFORMATION",
                            style = CoreTypography.caption,
                            color = CoreTheme.colors.textSecondary,
                        )

                        Column(verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm)) {
                            Column(verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs)) {
                                Text(
                                    text = "Full name",
                                    style = CoreTypography.caption,
                                    color = CoreTheme.colors.textSecondary,
                                )
                                Text(
                                    text = state.child.fullName,
                                    style = CoreTypography.body,
                                    color = CoreTheme.colors.textPrimary,
                                )
                            }

                            state.child.displayName?.let { displayName ->
                                Column(verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs)) {
                                    Text(
                                        text = "Display name",
                                        style = CoreTypography.caption,
                                        color = CoreTheme.colors.textSecondary,
                                    )
                                    Text(
                                        text = displayName,
                                        style = CoreTypography.body,
                                        color = CoreTheme.colors.textPrimary,
                                    )
                                }
                            }

                            Column(verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs)) {
                                Text(
                                    text = "Date of birth",
                                    style = CoreTypography.caption,
                                    color = CoreTheme.colors.textSecondary,
                                )
                                Text(
                                    text = state.child.formatedDateOfBirth,
                                    style = CoreTypography.body,
                                    color = CoreTheme.colors.textPrimary,
                                )
                            }
                        }
                    }
                }

                // Care Team Status Section
                if (state.careTeams.isNotEmpty()) {
                    // Display care teams list
                    Column(
                        verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
                    ) {
                        Text(
                            text = "CARE TEAMS",
                            style = CoreTypography.caption,
                            color = CoreTheme.colors.textSecondary,
                        )

                        state.careTeams.forEach { careTeam ->
                            val organizerMember = careTeam.members.firstOrNull { it.role == app.nestbridge.feature.careteam.model.TeamRelationshipRole.ORGANIZER }

                            CoreCard(
                                modifier = Modifier.fillMaxWidth(),
                            ) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(CoreSpacing.xs),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically,
                                ) {
                                    Column(
                                        modifier = Modifier.weight(1f),
                                        verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs),
                                    ) {
                                        Text(
                                            text = careTeam.teamType.label,
                                            style = CoreTypography.title,
                                            color = CoreTheme.colors.textPrimary,
                                        )
                                        if (organizerMember != null) {
                                            Text(
                                                text = "Organizer: ${organizerMember.userId}",
                                                style = CoreTypography.bodySecondary,
                                                color = CoreTheme.colors.textSecondary,
                                            )
                                        }
                                    }

                                    Text(
                                        text = "›",
                                        style = CoreTypography.title,
                                        color = CoreTheme.colors.textSecondary,
                                    )
                                }
                            }
                        }
                    }
                } else if (state.pendingInvitation != null) {
                    // Invitation Sent State
                    val formattedDestination = when (state.pendingInvitation.deliveryMethod) {
                        InvitationDeliveryMethod.PHONE ->
                            state.pendingInvitation.deliveryDestination?.let {
                                PhoneNumberFormatter.toDisplayFormat(it)
                            } ?: state.pendingInvitation.deliveryDestination
                        else -> state.pendingInvitation.deliveryDestination
                    }

                    CoreNoticeCard(
                        title = "INVITATION SENT",
                        message = "✉ Invitation sent to $formattedDestination. Waiting for the professional to accept and create a care team.",
                        tone = CoreNoticeTone.Info,
                        modifier = Modifier.fillMaxWidth(),
                    )

                    Column(
                        verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        CoreSecondaryButton(
                            leadingIcon = Icons.Default.QrCode2,
                            text = "View invitation QR code",
                            onClick = { state.eventSink(ChildDetailEvent.ViewInvitationQRCode) },
                            modifier = Modifier
                                .fillMaxWidth(),
                        )

                        CoreTextButton(
                            text = "Cancel invitation",
                            onClick = { state.eventSink(ChildDetailEvent.CancelInvitation) },
                        )
                    }
                } else {
                    // No Care Team State
                    CoreNoticeCard(
                        title = "CARE TEAM STATUS",
                        message = "No care team has been set up yet. You can invite a professional to create one, or create it yourself.",
                        tone = CoreNoticeTone.Warning,
                        modifier = Modifier.fillMaxWidth(),
                    )

                    Column(
                        verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
                    ) {
                        Text(
                            text = "What would you like to do?",
                            style = CoreTypography.body,
                            color = CoreTheme.colors.textPrimary,
                        )

                        CoreSecondaryButton(
                            leadingIcon = Icons.Default.Email,
                            iconSpacing = CoreSpacing.md,
                            text = "Invite a professional - Send an invitation to a provider to create a care team for ${state.child.effectiveDisplayName}.",
                            onClick = { state.eventSink(ChildDetailEvent.InviteProfessional) },
                            modifier = Modifier.fillMaxWidth()
                                .height(72.dp),
                        )

                        CoreSecondaryButton(
                            leadingIcon = Icons.Default.Group,
                            iconSpacing = CoreSpacing.md,
                            text = "Create care team myself - Set up a care team and invite professionals to join later.",
                            onClick = { state.eventSink(ChildDetailEvent.CreateCareTeamMyself) },
                            modifier = Modifier.fillMaxWidth()
                                .height(72.dp),
                        )
                    }
                }
            }
        }
    }
}

@Preview
@Composable
private fun ChildDetailUiWithCareTeamsPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        ChildDetailUi(
            state = ChildDetailState(
                child = Child(
                    id = "child-1",
                    fullName = "Sofia Grace Chen",
                    displayName = "Sofia",
                    dateOfBirth = LocalDate(2023, 1, 21),
                    primaryLanguages = emptyList(),
                    stewardUserIds = listOf("user-1"),
                    status = ChildStatus.Active,
                    createdAt = Clock.System.now(),
                    createdBy = "user-1",
                    modifiedAt = Clock.System.now(),
                    modifiedBy = "user-1",
                ),
                careTeams = listOf(
                    CareTeam(
                        id = "team-1",
                        childId = "child-1",
                        teamType = ProfessionalServiceType.SpeechLanguageTherapy,
                        description = "Working on articulation and language development",
                        members = listOf(
                            app.nestbridge.feature.careteam.model.CareTeamMember(
                                userId = "Dr. Sarah Johnson",
                                role = app.nestbridge.feature.careteam.model.TeamRelationshipRole.ORGANIZER,
                            ),
                        ),
                        status = "Active",
                        createdAt = Clock.System.now(),
                        createdBy = "user-1",
                        modifiedAt = Clock.System.now(),
                        modifiedBy = "user-1",
                    ),
                    CareTeam(
                        id = "team-2",
                        childId = "child-1",
                        teamType = ProfessionalServiceType.OccupationalTherapy,
                        description = "Focus on fine motor skills and sensory integration",
                        members = listOf(
                            app.nestbridge.feature.careteam.model.CareTeamMember(
                                userId = "Maria Rodriguez, OT",
                                role = app.nestbridge.feature.careteam.model.TeamRelationshipRole.ORGANIZER,
                            ),
                        ),
                        status = "Active",
                        createdAt = Clock.System.now(),
                        createdBy = "user-1",
                        modifiedAt = Clock.System.now(),
                        modifiedBy = "user-1",
                    ),
                ),
                pendingInvitation = null,
                isLoading = false,
                eventSink = {},
            ),
        )
    }
}

@Preview
@Composable
private fun ChildDetailUiPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        ChildDetailUi(
            state = ChildDetailState(
                child = Child(
                    id = "child-1",
                    fullName = "Sofia Grace Chen",
                    displayName = "Sofia",
                    dateOfBirth = LocalDate(2023, 1, 21),
                    primaryLanguages = emptyList(),
                    stewardUserIds = listOf("user-1"),
                    status = ChildStatus.Active,
                    createdAt = Clock.System.now(),
                    createdBy = "user-1",
                    modifiedAt = Clock.System.now(),
                    modifiedBy = "user-1",
                ),
                careTeams = emptyList(),
                pendingInvitation = null,
                isLoading = false,
                eventSink = {},
            ),
        )
    }
}

@Preview
@Composable
private fun ChildDetailUiWithInvitationViaPhonePreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        ChildDetailUi(
            state = ChildDetailState(
                child = Child(
                    id = "child-1",
                    fullName = "Sofia Grace Chen",
                    displayName = "Sofia",
                    dateOfBirth = LocalDate(2023, 1, 21),
                    primaryLanguages = emptyList(),
                    stewardUserIds = listOf("user-1"),
                    status = ChildStatus.Active,
                    createdAt = Clock.System.now(),
                    createdBy = "user-1",
                    modifiedAt = Clock.System.now(),
                    modifiedBy = "user-1",
                ),
                careTeams = emptyList(),
                pendingInvitation = Invitation(
                    id = "inv-1",
                    status = InvitationStatus.PENDING,
                    inviterUserProfileId = "user-1",
                    inviterDisplayName = "Parent",
                    childId = "child-1",
                    childDisplayName = "Sofia",
                    teamId = null,
                    teamType = null,
                    proposedTeamRole = app.nestbridge.feature.careteam.model.TeamRelationshipRole.ORGANIZER,
                    grantsChildStewardship = false,
                    deliveryMethod = InvitationDeliveryMethod.PHONE,
                    deliveryDestination = "+15551234567",
                    createdAt = Clock.System.now(),
                    createdBy = "user-1",
                    modifiedAt = Clock.System.now(),
                    modifiedBy = "user-1",
                ),
                isLoading = false,
                eventSink = {},
            ),
        )
    }
}

@Preview
@Composable
private fun ChildDetailUiWithInvitationViaEmailPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        ChildDetailUi(
            state = ChildDetailState(
                child = Child(
                    id = "child-1",
                    fullName = "Sofia Grace Chen",
                    displayName = "Sofia",
                    dateOfBirth = LocalDate(2023, 1, 21),
                    primaryLanguages = emptyList(),
                    stewardUserIds = listOf("user-1"),
                    status = ChildStatus.Active,
                    createdAt = Clock.System.now(),
                    createdBy = "user-1",
                    modifiedAt = Clock.System.now(),
                    modifiedBy = "user-1",
                ),
                careTeams = emptyList(),
                pendingInvitation = Invitation(
                    id = "inv-1",
                    status = InvitationStatus.PENDING,
                    inviterUserProfileId = "user-1",
                    inviterDisplayName = "Parent",
                    childId = "child-1",
                    childDisplayName = "Sofia",
                    teamId = null,
                    teamType = null,
                    proposedTeamRole = app.nestbridge.feature.careteam.model.TeamRelationshipRole.ORGANIZER,
                    grantsChildStewardship = false,
                    deliveryMethod = InvitationDeliveryMethod.EMAIL,
                    deliveryDestination = "therapist@example.com",
                    createdAt = Clock.System.now(),
                    createdBy = "user-1",
                    modifiedAt = Clock.System.now(),
                    modifiedBy = "user-1",
                ),
                isLoading = false,
                eventSink = {},
            ),
        )
    }
}
